import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { BeforeMount } from "@monaco-editor/react";
import { DsButton } from "../ds";
import { AlertIcon, FileIcon, FolderIcon, SearchIcon } from "../ds/icons";
import { TOAST_SURFACE } from "../ds/toast";
import {
  ApiV2Error,
  listSourceFiles,
  sourceFileContent,
  SourceFileContent,
} from "../../utils/apiV2";

/**
 * The app's uploaded code in a read-only editor — the tree on the left,
 * Monaco on the right, on the dashboard's own navy rather than Monaco's grey.
 * Fills whatever box it is given: the Source Code screen gives it the rest of
 * the window.
 *
 * File contents come from the API, one file at a time, never from a storage
 * link: access is checked per file, and binary or large files come back as a
 * notice instead of bytes.
 */

// Only loaded when someone opens the viewer.
const Editor = lazy(() => import("@monaco-editor/react"));

const THEME = "b4a-navy";

// The dashboard's tokens (tailwind.config.js navy scale) in Monaco's terms.
const defineTheme: BeforeMount = (monaco) => {
  monaco.editor.defineTheme(THEME, {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "keyword", foreground: "7EA6E8" },
      { token: "string", foreground: "E39A6F" },
      { token: "number", foreground: "9CCC65" },
      { token: "comment", foreground: "5E7493", fontStyle: "italic" },
      { token: "type", foreground: "6FC3D9" },
    ],
    colors: {
      "editor.background": "#0A1220",
      "editor.foreground": "#E8ECF3",
      "editorLineNumber.foreground": "#5E7493",
      "editorLineNumber.activeForeground": "#8FA2BD",
      "editor.lineHighlightBackground": "#111B2E",
      "editor.lineHighlightBorder": "#111B2E",
      "editor.selectionBackground": "#1E2E4999",
      "editorIndentGuide.background1": "#1E2E49",
      "scrollbarSlider.background": "#283A5880",
      "scrollbarSlider.hoverBackground": "#3B4E6D",
    },
  });
};

interface SourceFile {
  path: string;
  size: number;
}

interface TreeNode {
  name: string;
  path: string;
  file?: SourceFile;
  children: TreeNode[];
}

const LANGUAGES: Record<string, string> = {
  js: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  jsx: "javascript",
  ts: "typescript",
  tsx: "typescript",
  mts: "typescript",
  json: "json",
  html: "html",
  htm: "html",
  css: "css",
  scss: "scss",
  less: "less",
  md: "markdown",
  markdown: "markdown",
  xml: "xml",
  svg: "xml",
  yaml: "yaml",
  yml: "yaml",
  toml: "ini",
  ini: "ini",
  env: "ini",
  sh: "shell",
  bash: "shell",
  sql: "sql",
  graphql: "graphql",
  py: "python",
  go: "go",
  rb: "ruby",
  java: "java",
  kt: "kotlin",
  php: "php",
  rs: "rust",
  cs: "csharp",
  swift: "swift",
  scala: "scala",
  ex: "elixir",
  exs: "elixir",
};

const fileName = (path: string) => path.slice(path.lastIndexOf("/") + 1);

const extensionOf = (path: string) => {
  const name = fileName(path);
  return name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
};

const isDockerfile = (path: string) => {
  const name = fileName(path);
  return name === "Dockerfile" || name.endsWith(".dockerfile");
};

const languageFor = (path: string) => {
  if (isDockerfile(path)) return "dockerfile";
  if (fileName(path) === "Makefile") return "makefile";
  return LANGUAGES[extensionOf(path)] || "plaintext";
};

const buildTree = (files: SourceFile[]): TreeNode[] => {
  const root: TreeNode = { name: "", path: "", children: [] };
  for (const file of files) {
    let node = root;
    const segments = file.path.split("/");
    segments.forEach((segment, index) => {
      const isLeaf = index === segments.length - 1;
      const path = segments.slice(0, index + 1).join("/");
      let child = node.children.find(
        (c) => c.name === segment && !!c.file === isLeaf
      );
      if (!child) {
        child = {
          name: segment,
          path,
          children: [],
          file: isLeaf ? file : undefined,
        };
        node.children.push(child);
      }
      node = child;
    });
  }
  const sort = (nodes: TreeNode[]) => {
    // Folders first, then files, each alphabetical.
    nodes.sort((a, b) =>
      !!a.file === !!b.file ? a.name.localeCompare(b.name) : a.file ? 1 : -1
    );
    nodes.forEach((n) => sort(n.children));
  };
  sort(root.children);
  return root.children;
};

// Where the viewer opens: the file that says what the app is.
const INITIAL_FILES = [
  "Dockerfile",
  "package.json",
  "requirements.txt",
  "go.mod",
  "README.md",
];

// Up to this many files every folder starts open: the tree is short.
const ALL_OPEN_MAX_FILES = 200;

const ancestors = (path: string) =>
  path
    .split("/")
    .slice(0, -1)
    .map((_, i, parts) => parts.slice(0, i + 1).join("/"));

// Folders carry a disclosure triangle and a trailing slash; a file sits
// where a folder's name starts, so each level reads as one column.
const Tree = ({
  nodes,
  depth,
  open,
  selected,
  onToggle,
  onSelect,
}: {
  nodes: TreeNode[];
  depth: number;
  open: Set<string>;
  selected: string | null;
  onToggle: (path: string) => void;
  onSelect: (file: SourceFile) => void;
}) => (
  <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
    {nodes.map((node) => {
      // The disclosure column only where this level has a folder to open: a
      // plain list of files starts at the icon.
      const hasFolders = nodes.some((n) => !n.file);
      const isOpen = open.has(node.path);
      const isSelected = node.path === selected;
      return (
        <li key={`${node.file ? "f" : "d"}:${node.path}`}>
          <button
            type="button"
            onClick={() =>
              node.file ? onSelect(node.file) : onToggle(node.path)
            }
            title={node.path}
            className={`b4-focus w-full flex items-center gap-2 pr-2 h-9 rounded-control text-left font-courrier text-ds-125 ${
              isSelected
                ? "bg-navy-600 text-white"
                : node.file
                ? "text-navy-100 hover:bg-navy-700"
                : "text-navy-200 hover:bg-navy-700"
            }`}
            style={{ paddingLeft: 8 + depth * 18 }}
          >
            {hasFolders ? (
              <span
                className="w-3 flex-none text-[10px] text-navy-300"
                aria-hidden
              >
                {node.file ? "" : isOpen ? "\u25BE" : "\u25B8"}
              </span>
            ) : null}
            <span
              className={`flex-none ${
                isSelected ? "text-white" : "text-navy-300"
              }`}
            >
              {node.file ? <FileIcon size={14} /> : <FolderIcon size={14} />}
            </span>
            <span className="truncate">
              {node.file ? node.name : `${node.name}/`}
            </span>
          </button>
          {!node.file && isOpen ? (
            <Tree
              nodes={node.children}
              depth={depth + 1}
              open={open}
              selected={selected}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ) : null}
        </li>
      );
    })}
  </ul>
);

const Notice = ({ children }: { children: React.ReactNode }) => (
  <div className="h-full flex items-center justify-center px-6 text-center text-ds-125 text-navy-200">
    {children}
  </div>
);

const CodeViewer = ({
  appId,
  deploymentId,
  reloadKey,
  toolbarStart,
}: {
  appId: string;
  /** Phone: what goes before the file picker in the toolbar (the version). */
  toolbarStart?: React.ReactNode;
  /** One deployment's code; without it, the app's latest. */
  deploymentId?: string;
  /** Changes when the app has new code, so the files are listed again. */
  reloadKey?: string;
}) => {
  const [files, setFiles] = useState<SourceFile[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  // Phone: the tree is a picker over the editor, open only while choosing.
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<SourceFile | null>(null);
  const [content, setContent] = useState<SourceFileContent | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);

  const select = (file: SourceFile) => {
    setSelected(file);
    setIsPickerOpen(false);
    setOpenFolders(
      (prev) => new Set(Array.from(prev).concat(ancestors(file.path)))
    );
  };

  useEffect(() => {
    let cancelled = false;
    setFiles(null);
    setListError(null);
    setSelected(null);
    setContent(null);
    setOpenFolders(new Set());
    setFilter("");
    listSourceFiles(appId, deploymentId)
      .then(({ files: listed }) => {
        if (cancelled) return;
        setFiles(listed);
        // A short tree opens whole; a long one only on the way to the first file.
        if (listed.length <= ALL_OPEN_MAX_FILES) {
          setOpenFolders(new Set(listed.flatMap((f) => ancestors(f.path))));
        }
        const first =
          INITIAL_FILES.map((name) => listed.find((f) => f.path === name)).find(
            Boolean
          ) || listed[0];
        if (first) select(first);
      })
      .catch(
        (e) =>
          !cancelled &&
          setListError(
            e instanceof ApiV2Error
              ? e.message
              : "The files could not be loaded."
          )
      );
    return () => {
      cancelled = true;
    };
  }, [appId, deploymentId, reloadKey]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setContent(null);
    setContentError(null);
    sourceFileContent(appId, selected.path, deploymentId)
      .then((result) => !cancelled && setContent(result))
      .catch(
        (e) =>
          !cancelled &&
          setContentError(
            e instanceof ApiV2Error
              ? e.message
              : "This file could not be opened."
          )
      );
    return () => {
      cancelled = true;
    };
  }, [appId, selected, deploymentId]);

  const filtered = useMemo(() => {
    const query = filter.trim().toLowerCase();
    return (files || []).filter(
      (f) => !query || f.path.toLowerCase().includes(query)
    );
  }, [files, filter]);
  const tree = useMemo(() => buildTree(filtered), [filtered]);
  // While filtering, every folder with a match is open.
  const visibleOpen = useMemo(
    () =>
      filter.trim()
        ? new Set(filtered.flatMap((f) => ancestors(f.path)))
        : openFolders,
    [filter, filtered, openFolders]
  );

  const toggle = (path: string) =>
    setOpenFolders((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });

  const text = content && content.kind === "TEXT" ? content.content : null;

  const copy = async () => {
    if (text === null) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied", { className: TOAST_SURFACE });
    } catch (e) {
      toast.error("Could not copy to the clipboard.", {
        className: TOAST_SURFACE,
      });
    }
  };

  if (listError) {
    return (
      <div
        role="alert"
        className="flex items-center gap-2 text-ds-125 text-state-err"
      >
        <AlertIcon size={14} /> {listError}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-3">
      {/* Phone: the version and the file as two pickers above the code. */}
      <div className="hidden [@media(max-width:767.98px)]:flex flex-none items-stretch gap-2.5">
        {toolbarStart}
        <button
          type="button"
          aria-expanded={isPickerOpen}
          onClick={() => setIsPickerOpen((open) => !open)}
          className="b4-focus flex-1 min-w-0 h-11 flex items-center justify-between gap-2 rounded-card border border-navy-400 bg-navy-800 px-3.5 font-courrier text-ds-135 text-white"
        >
          <span className="truncate">{selected?.path || "Pick a file"}</span>
          <span className="flex-none text-[10px] text-navy-300" aria-hidden>
            {isPickerOpen ? "\u25B4" : "\u25BE"}
          </span>
        </button>
      </div>

      {/* Desktop: the tree beside the editor. Phone: the editor alone, and the
        tree over it while picking a file. */}
      <div className="relative flex flex-1 min-h-0 rounded-card border border-navy-500 overflow-hidden">
        <div
          className={`w-72 flex-none flex flex-col bg-navy-800 border-r border-navy-500 [@media(max-width:767.98px)]:absolute [@media(max-width:767.98px)]:inset-0 [@media(max-width:767.98px)]:z-10 [@media(max-width:767.98px)]:w-full [@media(max-width:767.98px)]:border-r-0 ${
            isPickerOpen ? "" : "[@media(max-width:767.98px)]:hidden"
          }`}
        >
          {/* The same height and rule as the editor's file bar beside it, so
            the two read as one line across the box. */}
          <div className="flex-none h-14 px-2.5 flex items-center border-b border-navy-500">
            <div className="w-full flex items-center gap-2 h-9 px-2.5 rounded-control bg-navy-900 border border-navy-500 focus-within:border-navy-300">
              <SearchIcon className="flex-none text-navy-300" />
              <input
                className="flex-1 min-w-0 bg-transparent border-0 outline-none font-courrier text-ds-125 text-white placeholder:text-navy-300"
                placeholder="Search files"
                aria-label="Search files"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-auto px-2.5 pt-3 pb-2.5">
            {!files ? (
              <div className="px-3 py-2 font-courrier text-ds-115 text-navy-300">
                Loading files…
              </div>
            ) : tree.length ? (
              <Tree
                nodes={tree}
                depth={0}
                open={visibleOpen}
                selected={selected?.path || null}
                onToggle={toggle}
                onSelect={select}
              />
            ) : (
              <div className="px-3 py-2 font-courrier text-ds-115 text-navy-300">
                No file matches.
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col bg-navy-900">
          <div className="flex-none flex items-center justify-between gap-3 h-14 px-5 border-b border-navy-500 [@media(max-width:767.98px)]:hidden">
            <span className="truncate font-courrier text-ds-125 text-white">
              {selected?.path || ""}
            </span>
            {text !== null && selected ? (
              <DsButton variant="outline" size="md" onClick={copy}>
                Copy
              </DsButton>
            ) : null}
          </div>
          {/* Phone: no file bar — the picker above names the file — so Copy
            floats over the code's corner. */}
          {text !== null && selected ? (
            <span className="hidden [@media(max-width:767.98px)]:block absolute top-2.5 right-2.5 z-[5]">
              <DsButton variant="outline" size="md" onClick={copy}>
                Copy
              </DsButton>
            </span>
          ) : null}
          <div className="flex-1 min-h-0">
            {contentError ? (
              <Notice>{contentError}</Notice>
            ) : !selected ? (
              <Notice>Pick a file on the left.</Notice>
            ) : !content ? (
              <Notice>Loading…</Notice>
            ) : content.kind !== "TEXT" ? (
              <Notice>
                {content.kind === "BINARY"
                  ? "This is a binary file. Use Download .zip to get it."
                  : "This file is larger than 1 MB, too large to show here. Use Download .zip to get it."}
              </Notice>
            ) : (
              <Suspense fallback={<Notice>Loading editor…</Notice>}>
                <Editor
                  height="100%"
                  path={content.path}
                  language={languageFor(content.path)}
                  // Without the file's final newline, so the last numbered
                  // line is the last line of text ("5 lines" shows 5).
                  // Copy keeps the file as it is.
                  value={content.content.replace(/\r?\n$/, "")}
                  theme={THEME}
                  beforeMount={defineTheme}
                  options={{
                    readOnly: true,
                    domReadOnly: true,
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineHeight: 21,
                    fontFamily:
                      "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
                    lineNumbers: "on",
                    lineNumbersMinChars: 4,
                    glyphMargin: false,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    padding: { top: 14, bottom: 14 },
                    renderLineHighlight: "none",
                    scrollbar: {
                      verticalScrollbarSize: 10,
                      horizontalScrollbarSize: 10,
                      useShadows: false,
                    },
                    overviewRulerBorder: false,
                    overviewRulerLanes: 0,
                    hideCursorInOverviewRuler: true,
                    quickSuggestions: false,
                    contextmenu: false,
                    folding: true,
                    showFoldingControls: "mouseover",
                  }}
                />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeViewer;
